// ============================================================================
// OINP — Ontario Workforce Priority stream
// Rule tables and point grids. DATA ONLY — no logic lives in this file.
// ============================================================================
//
// PROVENANCE POLICY (read before editing)
// ---------------------------------------
// Every point table carries a `provenance` tag:
//
//   'official'  — transcribed from the official source (O. Reg. 422/17 as
//                 amended, or the OINP Workforce Priority points grid on
//                 ontario.ca). Safe to score with, no caveat.
//   'secondary' — taken from a third-party rendering of the grid and
//                 cross-checked for internal consistency, but NOT read off
//                 the official source. Scoreable, but the UI shows an
//                 "estimate — verify" banner.
//   'pending'   — not yet supplied. Any cell whose value is PENDING is
//                 unscoreable. If the user's own answer lands on a PENDING
//                 cell the engine refuses to produce a total and reports the
//                 factor in `missingFactors` instead of guessing.
//
// NEVER replace a PENDING with a remembered, inferred, or "obvious" number.
// A wrong point value on an immigration eligibility tool is a real-world
// harm. Fill these in only from the official grid.
//
// TO POPULATE: replace PENDING with the integer from the official grid and
// flip that table's `provenance` to 'official'. No code changes required.
//
// ---------------------------------------------------------------------------
// STATUS as of 2026-07-28: every point table below is 'official', transcribed
// from the Ontario Workforce Priority stream page on ontario.ca. There are no
// PENDING cells left in the scoring grid. The transcription is guarded by
// `bun run test:oinp` in widget-server, which asserts the published worked
// example still totals 72 of 130 — so a mistyped cell fails a test rather than
// reaching a client.
//
// Two things are still genuinely unsourced and are marked at their definitions:
//   - SKILLED_TRADE_NOCS (which occupations get the lower language / education
//     bar) — empty, and handled by softening a fail into "needs verification".
//   - RURAL_REVENUE_REDUCTION (a possible lower employer revenue threshold for
//     small census divisions) — not applied.
// ============================================================================

/** Sentinel for a point value that has not been loaded from the official source. */
export const PENDING = null;

export const OINP_RULES = {
  version: '2026-07-28',
  stream: 'Ontario Workforce Priority',
  legalBasis: 'O. Reg. 422/17 as amended',
  officialUrl: 'https://www.ontario.ca/page/ontario-workforce-priority-stream',
  employerGuideUrl: 'https://www.ontario.ca/page/oinp-employer-job-offer-streams-employer-guide',
  /** Date the point tables below were transcribed from the official pages. */
  transcribedOn: '2026-07-28',
  maxScore: 130,
  /**
   * Self-employed physicians are scored on the same factors EXCEPT hourly wage,
   * which ontario.ca states is not applicable to them. Their ceiling is
   * therefore 130 - 15 = 115.
   */
  physicianMaxScore: 115,
};

// ---------------------------------------------------------------------------
// Factor maxima — 11 factors, 130 points.
// Verified: the maxima are stated consistently in the stream documentation and
// they sum to the published total, which is a real check on the whole set.
// ---------------------------------------------------------------------------
export const FACTORS = [
  { id: 'teer', max: 9, labelKey: 'factorTeer' },
  { id: 'occupationalCategory', max: 10, labelKey: 'factorOccupation' },
  { id: 'wage', max: 15, labelKey: 'factorWage' },
  { id: 'ontarioExperience', max: 18, labelKey: 'factorOntarioExp' },
  { id: 'canadianEarnings', max: 8, labelKey: 'factorEarnings' },
  { id: 'legalStatus', max: 10, labelKey: 'factorStatus' },
  { id: 'education', max: 10, labelKey: 'factorEducation' },
  { id: 'canadianCredentials', max: 10, labelKey: 'factorCanadianCreds' },
  { id: 'lowestClb', max: 15, labelKey: 'factorClb' },
  { id: 'officialLanguages', max: 10, labelKey: 'factorBilingual' },
  { id: 'region', max: 15, labelKey: 'factorRegion' },
];

export const MAX_SCORE = FACTORS.reduce((sum, f) => sum + f.max, 0); // 130

// ---------------------------------------------------------------------------
// 1. TEER category (max 9) — derived from the 2nd digit of the NOC 2021 code.
// ---------------------------------------------------------------------------
export const TEER_POINTS = {
  provenance: 'official',
  table: { 0: 9, 1: 9, 2: 6, 3: 6, 4: 0, 5: 0 },
};

// ---------------------------------------------------------------------------
// 2. Broad occupational category (max 10) — 1st digit of the NOC 2021 code.
// ---------------------------------------------------------------------------
export const OCCUPATIONAL_CATEGORY_POINTS = {
  provenance: 'official',
  // Health (3) and trades/transport (7) carry the most weight - this is the
  // "workforce priority" in the stream's name, expressed as points.
  table: { 0: 4, 1: 4, 2: 6, 3: 10, 4: 4, 5: 2, 6: 2, 7: 8, 8: 4, 9: 4 },
};

// Category names are official labels; only fill in ones read off the source.
// Unfilled entries render as "Category N", which is always true.
export const BROAD_CATEGORY_LABELS = {
  provenance: 'official',
  table: {
    0: 'Legislative and senior management occupations',
    1: 'Business, finance and administration occupations',
    2: 'Natural and applied sciences and related occupations',
    3: 'Health occupations',
    4: 'Occupations in education, law and social, community and government services',
    5: 'Occupations in art, culture, recreation and sport',
    6: 'Sales and service occupations',
    7: 'Trades, transport and equipment operators and related occupations',
    8: 'Natural resources, agriculture and related production occupations',
    9: 'Occupations in manufacturing and utilities',
  },
};

// ---------------------------------------------------------------------------
// 3. Hourly wage (max 15) — base hourly wage, no bonuses/commission/piece work.
// Cross-checked: $23/hr scored 5, and moving to $25/hr was worth +3 → 8. The
// band edges and the deltas agree, so the table is internally consistent.
// ---------------------------------------------------------------------------
export const WAGE_BANDS = {
  provenance: 'official',
  bands: [
    { min: 40, points: 15, label: '$40.00 or more' },
    { min: 35, points: 12, label: '$35.00 – $39.99' },
    { min: 30, points: 10, label: '$30.00 – $34.99' },
    { min: 25, points: 8, label: '$25.00 – $29.99' },
    { min: 20, points: 5, label: '$20.00 – $24.99' },
    { min: 0, points: 0, label: 'Below $20.00' },
  ],
};

// ---------------------------------------------------------------------------
// 4. Ontario work experience (max 18) — two inputs: months in the job-offer
// position, and total months worked in Ontario in any job. See the structure
// note on the table itself: they are alternative tiers, not a matrix.
// ---------------------------------------------------------------------------
export const MONTH_BANDS = ['0-5', '6-12', '13-24', '25+'];

export const ONTARIO_EXPERIENCE_POINTS = {
  provenance: 'official',
  // NOT a matrix and NOT additive - two MUTUALLY EXCLUSIVE tiers.
  //
  // The primary tier scores months worked in the JOB OFFER POSITION. Only when
  // that is under 6 months (or they are not currently in the position) does the
  // fallback tier apply, scoring total months worked anywhere in Ontario. The
  // two are never summed, which is why the form asks both questions but the
  // second one usually has no effect on the score.
  inPosition: { '25+': 18, '13-24': 15, '6-12': 12, '0-5': 0 },
  inOntario: { '25+': 12, '13-24': 9, '6-12': 6, '0-5': 0 },
  // Self-employed physicians: cumulative medical practice in Ontario replaces
  // the job-offer-position tier entirely.
  physicianPractice: { '25+': 18, '13-24': 15, '6-12': 12, '0-5': 0 },
};

// ---------------------------------------------------------------------------
// 5. Canadian employment earnings (max 8) — highest single year in the last 5,
// per CRA Notice of Assessment.
// ---------------------------------------------------------------------------
export const EARNINGS_BANDS = ['70k+', '50-69999', '30-49999', 'under-30k', 'none'];

export const CANADIAN_EARNINGS_POINTS = {
  provenance: 'official',
  // "Under $30k earnings in a year" scores 0, and having no earnings falls in
  // that same bucket - there is no separate published row for it.
  table: { '70k+': 8, '50-69999': 6, '30-49999': 4, 'under-30k': 0, 'none': 0 },
};

// ---------------------------------------------------------------------------
// 6. Legal status in Canada (max 10)
// ---------------------------------------------------------------------------
export const LEGAL_STATUSES = ['work-permit', 'study-permit', 'maintained', 'visitor', 'no-status', 'outside-canada'];

export const LEGAL_STATUS_POINTS = {
  provenance: 'official',
  // Published rows: valid work permit (10), valid study permit (5), no valid
  // work or study permit (0). Everything else therefore scores 0.
  //
  // IMPORTANT: scoring 0 here is NOT an eligibility failure. Applying from
  // outside Canada, or holding visitor or maintained status, simply earns no
  // points for this factor. The eligibility gate must not treat it as a blocker.
  table: {
    'work-permit': 10,
    'study-permit': 5,
    'maintained': 0,
    'visitor': 0,
    'no-status': 0,
    'outside-canada': 0,
  },
};

// ---------------------------------------------------------------------------
// 7. Highest education (max 10)
// ---------------------------------------------------------------------------
export const EDUCATION_LEVELS = [
  'doctorate', 'masters', 'univ-cert-above-bachelor', 'bachelor',
  'ontario-college-grad-cert', 'univ-cert-below-bachelor',
  'college-diploma', 'apprenticeship', 'high-school', 'less-than-high-school',
];

export const EDUCATION_POINTS = {
  provenance: 'official',
  // "Less than college or trade certificate" scores 0 - that one published row
  // covers both high school and less than high school.
  table: {
    'doctorate': 10,
    'masters': 8,
    'univ-cert-above-bachelor': 6,
    'bachelor': 6,
    'ontario-college-grad-cert': 5,
    'univ-cert-below-bachelor': 5,
    'college-diploma': 5,
    'apprenticeship': 5,
    'high-school': 0,
    'less-than-high-school': 0,
  },
};

// ---------------------------------------------------------------------------
// 8. Canadian post-secondary credentials (max 10) — 1+ year, eligible institution.
// Cross-checked: 1 credential scored 5, and adding a 2nd was worth +5 → 10.
// The zero case is NOT confirmed and stays PENDING.
// ---------------------------------------------------------------------------
export const CANADIAN_CREDENTIAL_COUNTS = ['0', '1', '2+'];

export const CANADIAN_CREDENTIALS_POINTS = {
  provenance: 'official',
  table: { '0': 0, '1': 5, '2+': 10 },
};

// ---------------------------------------------------------------------------
// 9. Lowest CLB across all four skills (max 15) — best single test, < 2 years
// old. Academic and One Skill Retake tests are not accepted.
// Cross-checked: CLB 6 scored 4, and moving to CLB 9+ was worth +11 → 15 (the
// factor maximum), so both endpoints agree.
// ---------------------------------------------------------------------------
export const CLB_BANDS = ['4-or-lower', '5', '6', '7', '8', '9+', 'untested'];

export const LOWEST_CLB_POINTS = {
  provenance: 'official',
  // Published rows stop at "CLB 5 or lower" = 0, so CLB 5, CLB 4 and below, and
  // no test at all all score zero for this factor.
  table: { '4-or-lower': 0, '5': 0, '6': 4, '7': 8, '8': 12, '9+': 15, 'untested': 0 },
};

// ---------------------------------------------------------------------------
// 10. Knowledge of both official languages (max 10) — CLB 6+ in all four
// skills in both English and French.
// Cross-checked: one language scored 5, adding a French test at CLB 6+ in all
// four skills was worth +5 → 10 (the maximum). Both cells confirmed by two
// independent numbers. The no-test-at-all case is not confirmed.
// ---------------------------------------------------------------------------
export const OFFICIAL_LANGUAGES_POINTS = {
  provenance: 'official',
  table: { 'both': 10, 'one': 5, 'untested': 0 },
};

// ---------------------------------------------------------------------------
// 11. Region of the job offer (max 15)
// Cross-checked: a GTA-except-Toronto offer scored 5, matching this table, and
// the 0-in-Toronto / 15-in-Northern spread is stated in the stream's own
// regionalization guidance.
// ---------------------------------------------------------------------------
export const REGIONS = [
  { id: 'northern', points: 15, labelKey: 'regionNorthern' },
  { id: 'eastern', points: 10, labelKey: 'regionEastern' },
  { id: 'central-outside-gta', points: 10, labelKey: 'regionCentral' },
  { id: 'southwestern', points: 10, labelKey: 'regionSouthwestern' },
  { id: 'gta-except-toronto', points: 5, labelKey: 'regionGtaExTo' },
  { id: 'toronto', points: 0, labelKey: 'regionToronto' },
];

export const REGION_POINTS = {
  provenance: 'official',
  table: REGIONS.reduce((acc, r) => { acc[r.id] = r.points; return acc; }, {}),
};

// ===========================================================================
// ELIGIBILITY GATES (pass/fail — separate from scoring)
// ===========================================================================

// The work-experience gate is an ORDERED list: the first satisfied option
// wins. Order is load-bearing, do not sort this array.
export const EXPERIENCE_OPTIONS = [
  { id: 'six-months-exact-position', passes: true, labelKey: 'expSixMonths' },
  { id: 'recent-grad-three-months', passes: true, labelKey: 'expRecentGrad' },
  { id: 'two-years-cumulative', passes: true, labelKey: 'expTwoYears' },
  { id: 'licensed-to-practise', passes: true, labelKey: 'expLicensed' },
  { id: 'none-yet', passes: false, labelKey: 'expNone' },
];

// Truck (73300) and bus/transit (73301) drivers must still satisfy the
// 6-month rule even when they hold the required licence.
export const LICENCE_EXEMPT_BLOCKED_NOCS = ['73300', '73301'];

// NOC 33102 (nurse aides, orderlies and personal support workers): an Ontario
// college certificate in a related field satisfies the education requirement
// even though the general TEER 3 rule asks for a 1-year credential.
export const EDUCATION_RULE_EXCEPTIONS = {
  '33102': { acceptsOntarioCollegeCertificate: true, noteKey: 'pswEducationNote' },
};

/**
 * The language minimum is NOT a flat CLB 6 - it varies by TEER, and the skilled
 * trades get a lower bar than other TEER 0-3 occupations.
 *
 * Identifying a "skilled trade" needs the official trades NOC list, which we do
 * not have. Until it is supplied, a TEER 0-3 applicant sitting exactly at CLB 5
 * is reported as NEEDS VERIFICATION rather than failed outright, because the
 * trades carve-out may cover them. See SKILLED_TRADE_NOCS below.
 */
export const LANGUAGE_MINIMUMS = {
  provenance: 'official',
  teer0to3: 6,
  teer0to3SkilledTrades: 5,
  teer4to5: 4,
};

/**
 * Education minimum, also TEER-dependent:
 *   TEER 0-3 (other than skilled trades) - a 1+ year post-secondary credential
 *   TEER 0-3 skilled trades              - a secondary (high school) diploma
 *   TEER 4-5                             - a secondary (high school) diploma
 */
export const EDUCATION_MINIMUMS = {
  provenance: 'official',
  teer0to3: 'post-secondary-1yr',
  teer0to3SkilledTrades: 'secondary',
  teer4to5: 'secondary',
};

/**
 * Which occupations the OINP treats as a "listed skilled trades occupation".
 *
 * Transcribed from the Workforce Priority stream page (2026-07-28). It is defined
 * by NOC GROUP, not by a flat list of codes — which is why this is a set of code
 * prefixes plus two exclusions rather than an array of 5-digit codes. NOC 2021
 * codes nest by prefix, so a prefix test is exactly equivalent to group
 * membership:
 *
 *   Major Group 72  Technical trades and transportation officers and controllers
 *                   — EXCLUDING Sub-Major Group 726
 *   Major Group 73  General trades
 *   Major Group 82  Supervisors in natural resources, agriculture and related
 *                   production
 *   Major Group 83  Occupations in natural resources and related production
 *   Major Group 93  Central control and process operators, and aircraft assembly
 *                   — EXCLUDING Sub-Major Group 932
 *   Minor Group 6320  Cooks, butchers, bakers
 *   Unit Group 62200  Chefs
 */
export const SKILLED_TRADE_NOC_GROUPS = {
  provenance: 'official',
  includePrefixes: ['72', '73', '82', '83', '93', '6320', '62200'],
  excludePrefixes: ['726', '932'],
};

/**
 * True when the NOC code is a listed skilled trades occupation. Skilled trades
 * get a lower language bar (CLB 5 rather than 6) and need only a secondary
 * school diploma rather than a 1+ year post-secondary credential.
 */
export function isSkilledTradeNoc(code) {
  const clean = String(code || '').trim();
  if (!/^\d{5}$/.test(clean)) return false;
  if (SKILLED_TRADE_NOC_GROUPS.excludePrefixes.some(p => clean.startsWith(p))) return false;
  return SKILLED_TRADE_NOC_GROUPS.includePrefixes.some(p => clean.startsWith(p));
}

// Retained for callers that only need the strictest case.
export const MIN_ELIGIBILITY_CLB = LANGUAGE_MINIMUMS.teer0to3;

export const ELIGIBILITY_GATES = [
  { id: 'work-experience', labelKey: 'gateExperience' },
  { id: 'language-minimum', labelKey: 'gateLanguage' },
  { id: 'post-secondary-credential', labelKey: 'gateCredential' },
  { id: 'eca', labelKey: 'gateEca' },
  { id: 'legal-status', labelKey: 'gateStatus' },
];

// Recent eligible Ontario graduates are exempt from the language TEST
// requirement for eligibility. Test scores still earn points (up to 25 across
// the two language factors), so we keep asking.
export const RECENT_GRAD_LANGUAGE_EXEMPT = true;

// ===========================================================================
// PHYSICIAN BRANCH (self-employed physicians in Ontario)
// ===========================================================================
// Employer-side requirements and the job offer do not apply to them. They ARE
// scored, on the same factors as everyone else except hourly wage, which
// ontario.ca states is not applicable — so their ceiling is
// OINP_RULES.physicianMaxScore (115), not 130. Their Ontario work experience is
// scored on cumulative medical practice in Ontario rather than months in a job
// offer position, and their regional points come from the registered OHIP
// practice address.
//
// This is why the physician branch of the wizard asks the full question set and
// not just OHIP/CPSO/practice months: three answers would leave 8 of the 11
// scoring factors blank and no score could be published.

export const CPSO_CERTIFICATES = [
  { id: 'independent', qualifies: true, labelKey: 'cpsoIndependent' },
  { id: 'academic', qualifies: true, labelKey: 'cpsoAcademic' },
  { id: 'provisional', qualifies: true, labelKey: 'cpsoProvisional' },
  { id: 'none-or-postgrad', qualifies: false, labelKey: 'cpsoNone' },
];

// ===========================================================================
// EMPLOYER ELIGIBILITY
// ===========================================================================

// Revenue and headcount thresholds are keyed by the WORK LOCATION, which is
// why the employer checker rewrites its own question text from answer 1.
export const EMPLOYER_LOCATION_TIERS = [
  {
    id: 'gta',
    revenueFloor: 1000000,
    minCanadianOrPrEmployees: 5,
    labelKey: 'locGta',
    detailKey: 'locGtaDetail',
  },
  {
    id: 'midsize',
    revenueFloor: 500000,
    minCanadianOrPrEmployees: 3,
    labelKey: 'locMidsize',
    detailKey: 'locMidsizeDetail',
  },
  {
    id: 'elsewhere',
    revenueFloor: 250000,
    minCanadianOrPrEmployees: 3,
    labelKey: 'locElsewhere',
    detailKey: 'locElsewhereDetail',
  },
];

export const EMPLOYER_THRESHOLDS_PROVENANCE = 'official';

/**
 * Verified against the OINP employer guide on 2026-07-28. Worth recording
 * because a third-party summary asserted the opposite and it would have been a
 * costly thing to believe:
 *
 *   - The three-tier model is CORRECT. $250,000 does NOT apply uniformly outside
 *     the GTA; the 14 named census divisions below sit at $500,000. Telling an
 *     Ottawa or Waterloo employer they need $250,000 would understate their
 *     requirement by half.
 *   - The revenue threshold must be met in the last TWO most recently completed
 *     fiscal years, not the most recent one.
 *   - A reduced threshold for rural communities (census divisions under 150,000
 *     population) appears in some summaries but is NOT stated on either the
 *     stream page or the employer guide. NOT implemented - see the note on
 *     RURAL_REVENUE_REDUCTION below.
 */
export const REVENUE_FISCAL_YEARS_NOTE = 'last two most recently completed fiscal years';

/**
 * RESOLVED 2026-07-28: there is no rural / small-census-division reduction.
 *
 * Some third-party summaries describe a lower revenue threshold for employers in
 * census divisions under 150,000 population. Both official pages were checked
 * directly — the Workforce Priority stream page and the employer guide — and
 * neither mentions any such reduction. The "under 150,000" metric appears to be
 * carried over from older regional initiatives and conflated with the 2026
 * redesign.
 *
 * $250,000 is the floor for locations outside the GTA and the 14 listed census
 * divisions. Deliberately not applied; kept as a named constant so the next
 * person to read a blog post about it finds this note instead of re-litigating it.
 */
export const RURAL_REVENUE_REDUCTION = null;

export const MIDSIZE_AREAS = [
  'Ottawa', 'Waterloo', 'Hamilton', 'Simcoe County', 'Middlesex', 'Niagara',
  'Essex', 'Wellington', 'Greater Sudbury', 'Frontenac', 'Peterborough',
  'Brant', 'Hastings', 'Thunder Bay',
];

export const GTA_AREAS = ['City of Toronto', 'Durham', 'Halton', 'Peel', 'York'];

export const EMPLOYER_MIN_YEARS_IN_BUSINESS = 3;
export const FULL_TIME_HOURS_PER_WEEK = 30;
export const FULL_TIME_HOURS_PER_YEAR = 1560;
export const REVENUE_FISCAL_YEARS_REQUIRED = 2;
export const CVOR_REQUIRED_NOCS = ['73300', '73301'];
export const CVOR_ACCEPTED_RATINGS = ['Excellent', 'Satisfactory'];
export const RECRUITMENT_MIN_METHODS = 2;
export const RECRUITMENT_MIN_WEEKS = 4;

// Each employer question declares how its answers affect the verdict:
//   'pass' — requirement met
//   'warn' — needs verification; verdict becomes 'needs-verification'
//   'fail' — hard disqualifier for this stream
// `dynamic` marks questions whose text is parameterized by the location tier.
export const EMPLOYER_QUESTIONS = [
  {
    id: 'location',
    dynamic: false,
    promptKey: 'empQLocation',
    helpKey: 'empQLocationHelp',
    options: EMPLOYER_LOCATION_TIERS.map(t => ({ id: t.id, verdict: 'pass', labelKey: t.labelKey, detailKey: t.detailKey })),
  },
  {
    id: 'years-in-business',
    promptKey: 'empQYears',
    options: [
      { id: 'yes', verdict: 'pass', labelKey: 'yes' },
      { id: 'no', verdict: 'fail', labelKey: 'no', reasonKey: 'empFailYears' },
      { id: 'purchased-or-amalgamated', verdict: 'warn', labelKey: 'empYearsPurchased', detailKey: 'empYearsPurchasedDetail', reasonKey: 'empWarnYears' },
    ],
  },
  {
    id: 'revenue',
    dynamic: true, // threshold substituted from the location tier
    promptKey: 'empQRevenue',
    options: [
      { id: 'yes', verdict: 'pass', labelKey: 'yes' },
      { id: 'no', verdict: 'fail', labelKey: 'no', reasonKey: 'empFailRevenue' },
      { id: 'close', verdict: 'warn', labelKey: 'empRevenueClose', detailKey: 'empRevenueCloseDetail', reasonKey: 'empWarnRevenue' },
    ],
  },
  {
    id: 'employees',
    dynamic: true, // headcount substituted from the location tier
    promptKey: 'empQEmployees',
    helpKey: 'empQEmployeesHelp',
    options: [
      { id: 'yes', verdict: 'pass', labelKey: 'yes' },
      { id: 'no', verdict: 'fail', labelKey: 'no', reasonKey: 'empFailEmployees' },
    ],
  },
  {
    id: 'esa-ohsa',
    promptKey: 'empQOrders',
    helpKey: 'empQOrdersHelp',
    // Non-disqualifying answer first, deliberately.
    options: [
      { id: 'no', verdict: 'pass', labelKey: 'no' },
      { id: 'yes', verdict: 'fail', labelKey: 'yes', reasonKey: 'empFailOrders' },
    ],
  },
  {
    id: 'job-offer-shape',
    promptKey: 'empQFullTime',
    helpKey: 'empQFullTimeHelp',
    options: [
      { id: 'yes', verdict: 'pass', labelKey: 'yes' },
      { id: 'no', verdict: 'fail', labelKey: 'no', reasonKey: 'empFailFullTime' },
    ],
  },
  {
    id: 'wage-level',
    promptKey: 'empQWage',
    helpKey: 'empQWageHelp',
    options: [
      { id: 'at-or-above-median', verdict: 'pass', labelKey: 'empWageMedian' },
      { id: 'recent-grad-low-wage', verdict: 'pass', labelKey: 'empWageRecentGrad' },
      { id: 'collective-agreement', verdict: 'pass', labelKey: 'empWageCollective' },
      { id: 'not-sure', verdict: 'warn', labelKey: 'empWageNotSure', reasonKey: 'empWarnWage' },
    ],
  },
  {
    id: 'recruitment-waiver',
    promptKey: 'empQAuthorized',
    helpKey: 'empQAuthorizedHelp',
    options: [
      // 'no' is not a failure — it means the recruitment requirement applies.
      { id: 'yes', verdict: 'pass', labelKey: 'yes' },
      { id: 'no', verdict: 'warn', labelKey: 'no', reasonKey: 'empWarnRecruitment' },
    ],
  },
  {
    id: 'cvor',
    promptKey: 'empQCvor',
    options: [
      { id: 'not-applicable', verdict: 'pass', labelKey: 'no' },
      { id: 'yes-with-cvor', verdict: 'pass', labelKey: 'empCvorYes' },
      { id: 'yes-without-cvor', verdict: 'fail', labelKey: 'empCvorNo', reasonKey: 'empFailCvor' },
    ],
  },
];

// ===========================================================================
// EMPLOYER CHECKLIST — the four-phase to-do list the candidate hands their
// employer. Content, not point values, so it lives here as plain text.
//
// KEEP IN SYNC with SMTP_Service/src/tamplates/templates/OinpTemplates.ts, which
// renders the same list into the emailed version. If a threshold in here changes
// it changes in the engine tables first, and both of these follow.
// ===========================================================================
export const EMPLOYER_CHECKLIST_PHASES = [
  {
    title: 'Phase 1 — Before touching the portal (confirm eligibility)',
    items: [
      { text: 'Business active for 3+ years', detail: 'A recently purchased or amalgamated business can count its predecessor’s continuous operation if the same products or services continued.' },
      { text: 'A business premises in Ontario where the employee will work' },
      { text: 'No outstanding orders under the Employment Standards Act or the Occupational Health and Safety Act' },
      { text: 'Revenue meets the threshold for the work location', detail: '$1M (GTA) · $500K (14 listed mid-size areas) · $250K (elsewhere), in each of the two most recently completed fiscal years.' },
      { text: 'Enough full-time Canadian citizen / PR employees at that work location', detail: '5 in the GTA, 3 anywhere else. 30+ hrs/week; temporary foreign workers and part-timers do not count.' },
      { text: 'If the position is NOC 73300 or 73301: a valid CVOR certificate rated Excellent or Satisfactory' },
      { text: 'If the employee is not already authorised to work in Ontario and there is no positive LMIA: complete recruitment BEFORE making the offer', detail: 'At least 2 advertising methods, posted 4+ weeks, with the required ad content. Never conducted by an immigration representative — that causes refusal.' },
    ],
  },
  {
    title: 'Phase 2 — Portal registration (one-time)',
    items: [
      { text: 'An authorised signing officer creates the Ontario.ca Login and registers the business', detail: 'They must have legal authority to bind the company. NEVER the employee being nominated — if they register, or are listed as a signing officer or contact, the OINP deactivates the account and every job offer, EOI and application attached to it.' },
      { text: 'CRA business number to hand', detail: 'One account per CRA number. Franchisees register as themselves, not as the franchisor.' },
      { text: 'Enter registration details carefully', detail: 'They cannot be edited in the portal afterwards — changes require a webform request with supporting documents.' },
      { text: 'Complete the Employer Profile early (optional but strongly recommended)', detail: 'It pre-fills every future application: incorporation date, legal structure, incorporation number, fiscal year dates, revenue range, NAICS code, business description, locations, employee count, and the ESA/OHSA declarations.' },
      { text: 'Add employer contacts if others will help manage job offers', detail: 'Up to 2 additional signing officers. A representative can be appointed per job offer but cannot create the draft or sign the final declaration.' },
    ],
  },
  {
    title: 'Phase 3 — Submit the job offer (this starts the clock)',
    items: [
      { text: 'Pick the right option: TEER 0–3 or TEER 4–5', detail: 'Based on the job’s NOC. It cannot be changed once the draft is created.' },
      { text: 'Enter the employee’s name EXACTLY as it appears on their government ID', detail: 'Matching their e-Filing profile if they have one, plus their personal email address — never a representative’s email.' },
      { text: 'Job details: 5-digit NOC, hourly base wage, full-time (30+ hrs/week, 1,560+ hrs/year), permanent, work location, GTA yes/no', detail: 'No bonuses, commission or piece work counted.' },
      { text: 'Know before submitting: a submitted job offer cannot be edited', detail: 'Fixing anything means withdrawing and recreating it — which also cancels the employee’s EOI.' },
      { text: 'After submitting, the employee receives a Job Offer ID by email and has 30 days to register their EOI, or the offer expires' },
      { text: 'The EOI stays valid up to 12 months. If no invitation arrives, both it and the job offer expire and you start fresh.' },
    ],
  },
  {
    title: 'Phase 4 — If your employee is invited (the deadlines are strict)',
    items: [
      { text: 'Submit the application for approval of the employment position within 14 calendar days of the invitation', detail: 'Miss it and the employee’s application is withdrawn.' },
      { text: 'The employee can only submit after you do', detail: 'They get 17 days in total — every day you wait squeezes them.' },
      { text: 'Supporting documents scanned and legible, in English or French, max 10 MB per file', detail: 'Accepted: pdf, doc/docx, xls/xlsx, jpeg, png and more.' },
      { text: 'Application details prepared', detail: 'Duties and responsibilities, why the position is urgently necessary, weeks per year and hours per week, the Canadian citizen / PR employee count at the location, the employee’s current employment details if they already work there, licensing confirmation if the occupation is regulated, the equity declaration (employee plus family under 10%), the LMIA questions, and a recruitment summary if applicable.' },
      { text: 'Trucking positions (NOC 73300): also declare weekly driving hours, mileage, and minimum/maximum working hours' },
      { text: 'The signing officer prints the Consent Form, signs it in ink, uploads it, then clicks Submit', detail: 'Only the signing officer can do this — not a contact, and not a representative.' },
      { text: 'After nomination: keep the employee in the approved position at the approved wage, report any change to the terms of employment to the OINP immediately, and respond to Post-Nomination Verification Unit follow-ups' },
    ],
  },
];

// ===========================================================================
// DEADLINE CHAIN — every hop after the employer submits the job offer.
// These are the numbers a candidate loses their nomination over, so they are
// surfaced on the results screen rather than buried in an FAQ.
// ===========================================================================
export const DEADLINES = [
  { id: 'employer-job-offer', value: null, unitKey: null, textKey: 'dlEmployerFirst' },
  { id: 'register-eoi', value: 30, unitKey: 'days', textKey: 'dlRegisterEoi' },
  { id: 'eoi-validity', value: 12, unitKey: 'months', textKey: 'dlEoiValidity' },
  { id: 'employer-application', value: 14, unitKey: 'days', textKey: 'dlEmployerApplication' },
  { id: 'candidate-application', value: 17, unitKey: 'days', textKey: 'dlCandidateApplication' },
  { id: 'ircc-application', value: 6, unitKey: 'months', textKey: 'dlIrccApplication' },
  { id: 'express-entry-nominee', value: null, unitKey: null, textKey: 'dlExpressEntry' },
];

export const EXPRESS_ENTRY_NOMINATION_BONUS_CRS = 600;

// ===========================================================================
// Aggregate provenance — used by the UI to decide which caveat banner to show.
// ===========================================================================
export const RULE_TABLES = {
  teer: TEER_POINTS,
  occupationalCategory: OCCUPATIONAL_CATEGORY_POINTS,
  wage: WAGE_BANDS,
  ontarioExperience: ONTARIO_EXPERIENCE_POINTS,
  canadianEarnings: CANADIAN_EARNINGS_POINTS,
  legalStatus: LEGAL_STATUS_POINTS,
  education: EDUCATION_POINTS,
  canadianCredentials: CANADIAN_CREDENTIALS_POINTS,
  lowestClb: LOWEST_CLB_POINTS,
  officialLanguages: OFFICIAL_LANGUAGES_POINTS,
  region: REGION_POINTS,
};

/**
 * Weakest provenance across every table — 'pending' beats 'secondary' beats
 * 'official'. The results screen keys its disclaimer off this.
 */
export function overallProvenance() {
  const ranks = { official: 0, secondary: 1, pending: 2 };
  let worst = 'official';
  for (const table of Object.values(RULE_TABLES)) {
    if (ranks[table.provenance] > ranks[worst]) worst = table.provenance;
  }
  return worst;
}
